import round2Decimals from "../../../../../Components/round2Decimals";

// Helpers puros del modal Ver Insumo. Sin React y sin axios a proposito: la aritmetica de
// impuestos y de margen es lo unico del modal que puede estar mal en silencio, asi que vive
// aparte para poder revisarse (y ejecutarse) sin montar el componente.

/**
 * Aplica los impuestos del insumo sobre una base de precio y una base de costo.
 * - tasaCuota 1: base * %
 * - tasaCuota 2: (base + acumulado) * %   -> impuesto sobre impuesto
 * - tasaCuota 3: monto fijo / tipoDeCambio
 * - tasaCuota 5: exento, 0
 *
 * El acumulado se lleva por separado para precio y para costo porque las dos bases son
 * distintas y tasaCuota 2 depende del acumulado de SU propia base.
 */
export const calcularImpuestosSobreBases = (
  basePrecio,
  baseCosto,
  impuestos,
  tipoDeCambio
) => {
  const lista = Array.isArray(impuestos) ? impuestos : [];
  let totalPrecio = 0;
  let totalCosto = 0;

  lista.forEach((imp) => {
    let impPrecio = 0;
    let impCosto = 0;

    if (imp.tasaCuota === 1) {
      impPrecio = Number(basePrecio || 0) * (Number(imp.monto) / 100);
      impCosto = Number(baseCosto || 0) * (Number(imp.monto) / 100);
    } else if (imp.tasaCuota === 2) {
      impPrecio = (Number(basePrecio || 0) + totalPrecio) * (Number(imp.monto) / 100);
      impCosto = (Number(baseCosto || 0) + totalCosto) * (Number(imp.monto) / 100);
    } else if (imp.tasaCuota === 3) {
      const fijo = Number(imp.monto) / (tipoDeCambio || 1);
      impPrecio = fijo;
      impCosto = fijo;
    }
    // tasaCuota 5 (exento) y cualquier otra: 0

    totalPrecio += impPrecio;
    totalCosto += impCosto;
  });

  return {
    precioConImpuestos: round2Decimals(Number(basePrecio || 0) + totalPrecio),
    costoConImpuestos: round2Decimals(Number(baseCosto || 0) + totalCosto),
    totalImpuestosPrecio: round2Decimals(totalPrecio),
    totalImpuestosCosto: round2Decimals(totalCosto),
  };
};

/**
 * El margen de la empresa se interpreta de dos maneras segun `calcularPreciosSobre`:
 * - true  -> el % es sobre el COSTO:  precio = costo * (1 + p)
 * - false -> el % es sobre la VENTA:  precio = costo / (1 - p)
 *
 * Con p >= 100 sobre venta el denominador se vuelve cero o negativo: se devuelve 0 en vez de
 * Infinity para no escribir basura en el renglon.
 */
export const precioDesdePorcentaje = (costo, porcentaje, calcularPreciosSobre) => {
  const p = Number(porcentaje || 0) / 100;
  const base = Number(costo || 0);

  if (calcularPreciosSobre) {
    return round2Decimals(base * (1 + p));
  }

  const denominador = 1 - p;
  if (denominador <= 0) return 0;
  return round2Decimals(base / denominador);
};

/** Texto legible de un impuesto para la banda de identidad y el detalle. */
export const etiquetaImpuesto = (imp, tipoDeCambio) => {
  if (!imp) return "";
  if (imp.tasaCuota === 1) return `${imp.monto}%`;
  if (imp.tasaCuota === 2) return `${imp.monto}% acum.`;
  if (imp.tasaCuota === 3) {
    return `$ ${round2Decimals(Number(imp.monto) / (tipoDeCambio || 1))}`;
  }
  if (imp.tasaCuota === 5) return "Exento";
  return "";
};

export const nombreImpuesto = (imp, idx) =>
  imp?.nombre || imp?.clave || `Impuesto ${idx + 1}`;

/**
 * Une las listas de precios de la EMPRESA con los precios configurados del insumo (o de la
 * equivalencia) y calcula, para cada una, las dos cifras que muestra la tarjeta:
 *
 * - configurado: lo que de verdad esta guardado en precios[] (la verdad del catalogo)
 * - sugerido:    lo que deberia costar con el costo de HOY y el margen objetivo
 *                (`porcentajeFijo`), que es la misma cuenta que hace Listas De Precios
 *
 * Si el insumo no tiene entrada para esa lista, no hay configurado y el sugerido se calcula
 * con el porcentaje por defecto de la lista de la empresa.
 */
export const construirFilasDePrecios = ({
  listasEmpresa,
  preciosInsumo,
  costo,
  impuestos,
  calcularPreciosSobre,
  tipoDeCambio,
}) => {
  const listas = Array.isArray(listasEmpresa) ? listasEmpresa : [];
  const propios = Array.isArray(preciosInsumo) ? preciosInsumo : [];

  return listas.map((lista) => {
    const entrada = propios.find(
      (p) => p.precioId?.toString() === lista._id?.toString()
    );

    const configurado = entrada ? Number(entrada.precio || 0) : null;
    const porcentajeConfigurado = entrada ? entrada.porcentaje : null;

    // El margen objetivo es porcentajeFijo; si el insumo no lo tiene, el de la empresa.
    const porcentajeObjetivo =
      entrada && entrada.porcentajeFijo != null
        ? entrada.porcentajeFijo
        : lista.porcentaje;

    const sugerido = precioDesdePorcentaje(
      costo,
      porcentajeObjetivo,
      calcularPreciosSobre
    );

    const netoConfigurado =
      configurado != null
        ? calcularImpuestosSobreBases(configurado, 0, impuestos, tipoDeCambio)
            .precioConImpuestos
        : null;

    const netoSugerido = calcularImpuestosSobreBases(
      sugerido,
      0,
      impuestos,
      tipoDeCambio
    ).precioConImpuestos;

    const diferencia =
      configurado != null ? round2Decimals(configurado - sugerido) : 0;

    return {
      precioId: lista._id,
      nombre: lista.nombre,
      configurado,
      netoConfigurado,
      porcentajeConfigurado,
      porcentajeObjetivo,
      sugerido,
      netoSugerido,
      diferencia,
      desfasado: configurado != null && Math.abs(diferencia) >= 0.01,
    };
  });
};
